import { Pressable, Text, View } from "react-native";
import { ArrowRight } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { OrganicMiniIcon, type OrganicMiniIconKind } from "./OrganicMiniIcon";
import { HorizontalSnapCarousel } from "./HorizontalSnapCarousel";
import { TimelineSubtaskCard } from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import {
  BiologyDeepCarousel,
  type DeepBiologySlide,
} from "./EggFreezingBiologyDeepCarouselCard";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
export type SpermCarouselItem = {
  id: string;
  label: string;
  title: string;
  body: string;
  callout?: string;
  icon: OrganicMiniIconKind;
  cta?: PlanAction;
};

function buildSpermBiologyDetail(
  t: TranslationFunction,
  title: string,
  label: string,
  body: string,
  callout: string,
) {
  return {
    title,
    sections: [
      { title: label, body },
      {
        title: t("homepage.sperm_freezing_understand.what_matters_here_label"),
        body: callout,
      },
    ],
  };
}

export function buildSpermFreezingBiologySlides(
  t: TranslationFunction,
): DeepBiologySlide[] {
  const ns = "homepage.sperm_freezing_understand";
  const whatMattersHere = t(`${ns}.what_matters_here_label`);
  return [
    {
      id: "continuous-production",
      visual: "quality",
      label: t("homepage.sperm_freezing_understand.turnover_label"),
      title: t("homepage.sperm_freezing_understand.continuous_title"),
      body: t("homepage.sperm_freezing_understand.continuous_body"),
      calloutLabel: whatMattersHere,
      callout: t("homepage.sperm_freezing_understand.continuous_callout"),
      detail: buildSpermBiologyDetail(
        t,
        t(`${ns}.continuous_title`),
        t(`${ns}.turnover_label`),
        t(`${ns}.continuous_body`),
        t(`${ns}.continuous_callout`),
      ),
    },
    {
      id: "semen-analysis",
      visual: "monitoring",
      label: t("homepage.shared.baseline"),
      title: t("homepage.sperm_freezing_understand.semen_analysis_title"),
      body: t("homepage.sperm_freezing_understand.semen_analysis_body"),
      calloutLabel: whatMattersHere,
      callout: t("homepage.sperm_freezing_understand.semen_analysis_callout"),
      cta: {
        label: t("homepage.shared.check_semen_testing"),
        screen: "AtHomeFertilityTesting",
        params: { focus: "semen-analysis" },
      },
      detail: buildSpermBiologyDetail(
        t,
        t(`${ns}.semen_analysis_title`),
        t("homepage.shared.baseline"),
        t(`${ns}.semen_analysis_body`),
        t(`${ns}.semen_analysis_callout`),
      ),
    },
    {
      id: "collection",
      visual: "retrieval",
      label: t("homepage.sperm_freezing_understand.collection_label"),
      title: t("homepage.sperm_freezing_understand.collection_title"),
      body: t("homepage.sperm_freezing_understand.collection_body"),
      calloutLabel: whatMattersHere,
      callout: t("homepage.sperm_freezing_understand.collection_callout"),
      detail: buildSpermBiologyDetail(
        t,
        t(`${ns}.collection_title`),
        t(`${ns}.collection_label`),
        t(`${ns}.collection_body`),
        t(`${ns}.collection_callout`),
      ),
    },
    {
      id: "freezing",
      visual: "freezing",
      label: t("homepage.shared.freezing"),
      title: t("homepage.sperm_freezing_understand.freezing_title"),
      body: t("homepage.sperm_freezing_understand.freezing_body"),
      calloutLabel: whatMattersHere,
      callout: t("homepage.sperm_freezing_understand.freezing_callout"),
      detail: buildSpermBiologyDetail(
        t,
        t(`${ns}.freezing_title`),
        t("homepage.shared.freezing"),
        t(`${ns}.freezing_body`),
        t(`${ns}.freezing_callout`),
      ),
    },
    {
      id: "future-use",
      visual: "maturity",
      label: t("homepage.shared.future_use"),
      title: t("homepage.sperm_freezing_understand.future_use_title"),
      body: t("homepage.sperm_freezing_understand.future_use_body"),
      calloutLabel: whatMattersHere,
      callout: t("homepage.sperm_freezing_understand.future_use_callout"),
      detail: buildSpermBiologyDetail(
        t,
        t(`${ns}.future_use_title`),
        t("homepage.shared.future_use"),
        t(`${ns}.future_use_body`),
        t(`${ns}.future_use_callout`),
      ),
    },
    {
      id: "storage",
      visual: "attrition",
      label: t("homepage.sperm_freezing_understand.storage_label"),
      title: t("homepage.sperm_freezing_understand.storage_title"),
      body: t("homepage.sperm_freezing_understand.storage_body"),
      calloutLabel: whatMattersHere,
      callout: t("homepage.sperm_freezing_understand.storage_callout"),
      detail: buildSpermBiologyDetail(
        t,
        t(`${ns}.storage_title`),
        t(`${ns}.storage_label`),
        t(`${ns}.storage_body`),
        t(`${ns}.storage_callout`),
      ),
    },
  ];
}

function useSpermFreezingBiologySlides(): DeepBiologySlide[] {
  const t = useTranslations();
  return buildSpermFreezingBiologySlides(t);
}

function SpermCarouselCard({
  item,
  width,
  equalizedHeight,
  onAction,
}: {
  item: SpermCarouselItem;
  width: number;
  equalizedHeight?: number;
  onAction: (action: PlanAction) => void;
}) {
  const capturePress = usePressCapture("SpermCarouselCard");
  return (
    <View
      style={{
        width,
        minHeight: equalizedHeight ?? 276,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: "rgba(92, 52, 32, 0.1)",
        backgroundColor: "rgba(255,255,255,0.58)",
        padding: 15,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 10,
        }}
      >
        <View
          style={{
            alignSelf: "flex-start",
            borderRadius: 999,
            backgroundColor: "rgba(92, 52, 32, 0.07)",
            paddingHorizontal: 8,
            paddingVertical: 4,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              color: EMBER.colors.textLight,
              fontWeight: "800",
              textTransform: "uppercase",
            }}
          >
            {item.label}
          </Text>
        </View>
        <OrganicMiniIcon kind={item.icon} size={40} />
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 17,
          lineHeight: 22,
          color: EMBER.colors.text,
          fontWeight: "800",
          marginTop: 12,
        }}
      >
        {item.title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          lineHeight: 19,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 8,
        }}
      >
        {item.body}
      </Text>

      {item.callout ? (
        <View
          style={{
            borderRadius: 14,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.5)",
            backgroundColor: "rgba(255,255,255,0.42)",
            padding: 10,
            marginTop: 12,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
              fontWeight: "600",
            }}
          >
            {item.callout}
          </Text>
        </View>
      ) : null}

      {item.cta ? (
        <Pressable
          onPress={() => {
            if (!item.cta) return;
            capturePress(pressLabel(item.cta.label), "sperm_carousel_cta");
            onAction(item.cta);
          }}
          style={({ pressed }) => ({
            marginTop: "auto",
            paddingTop: 14,
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            opacity: pressed ? 0.72 : 1,
          })}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: EMBER.colors.text,
              fontWeight: "800",
            }}
          >
            {item.cta.label}
          </Text>
          <ArrowRight size={13} color={EMBER.colors.text} strokeWidth={1.8} />
        </Pressable>
      ) : null}
    </View>
  );
}

export function SpermCarousel({
  items,
  onAction,
}: {
  items: SpermCarouselItem[];
  onAction: (action: PlanAction) => void;
}) {
  return (
    <HorizontalSnapCarousel
      items={items}
      gap={10}
      fallbackCardWidth={250}
      getCardWidth={(containerWidth) =>
        Math.min(278, Math.max(238, containerWidth * 0.84))
      }
      keyExtractor={(item) => item.id}
      equalizeItemHeights
      measureKey={items.map((item) => item.id).join("|")}
      renderItem={(item, width, _index, equalizedHeight) => (
        <SpermCarouselCard
          item={item}
          width={width}
          equalizedHeight={equalizedHeight}
          onAction={onAction}
        />
      )}
      contentContainerStyle={{ paddingRight: 2 }}
    />
  );
}

export function UnderstandSpermFreezingStep({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const slides = useSpermFreezingBiologySlides();
  return (
    <StepperSection
      id="precycle-sperm-freezing.understand"
      stepLabel={t("homepage.sperm_freezing_understand.step_label")}
      showRail={false}
    >
      <TimelineSubtaskCard
        id="precycle-sperm-freezing.understand.how-it-works"
        title={t("homepage.sperm_freezing_understand.how_it_works_title")}
        subtitle={t("homepage.sperm_freezing_understand.how_it_works_subtitle")}
        titleIcon={<OrganicMiniIcon kind="sperm" size={34} />}
        collapsedPreviewHeight={68}
      >
        <BiologyDeepCarousel
          slides={slides}
          onAction={onAction}
          showEstimateBridge={false}
          illustrationVariant="organic-explanatory"
        />
      </TimelineSubtaskCard>
    </StepperSection>
  );
}
